import {
  Button,
  useDisclosure
} from '@chakra-ui/react'
import { CustomCollapse } from 'BrokerTsLib/src/components'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type {
  InsuranceParamsPropertyDto
} from 'BrokerTsLib/src/dtos'
import {
  InitialGeneralParamsPropertyValues,
  InitialLiabilityValues,
  InitialPolicyHolderValues,
  InitialPropertyValues
} from 'BrokerTsLib/src/dtos'
import {
  Product,
  RiskVariant
} from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import {
  FormProvider,
  useForm,
  yupResolver
} from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import {
  GeneralParamsValidationSchema,
  PolicyHolderLiabilityValidationSchema,
  PolicyHolderPropertyValidationSchema,
} from 'BrokerTsLib/src/validation'
import { PropertyValidationSchema } from 'BrokerTsLib/src/validation/property/PropertyValidationSchema'
import _ from 'lodash'
import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import * as yup from 'yup'

import { useApp } from '@/hooks/useApp'

import ErrorSummary from '../../../components/ErrorSummary'
import { MainContent } from '../../../components/MainContent'
import {
  useInsurers
} from '../../../hooks'
import { Check, Search } from '../../../icons'
import { useInsuranceParamsContext } from '../../../layouts/Wizard'
import { FormButtons } from '../../FormButtons'
import { BaseParametersForm } from './BaseParametersForm'
import { HouseholdForm } from './HouseholdForm'
import { LocationForm } from './LocationForm'
import { PolicyHolderForm } from './PolicyHolderForm'
import { RealtyForm } from './RealtyForm'

interface Step1FormProps {
  onSubmit: (values: InsuranceParamsPropertyDto) => Promise<any>
  data?: InsuranceParamsPropertyDto
}

export const schema = yup.object().shape({
  generalParams: GeneralParamsValidationSchema,
  policyHolder: PolicyHolderPropertyValidationSchema,
  property: PropertyValidationSchema,
})

export const schemaLiability = yup.object().shape({
  policyHolder: PolicyHolderLiabilityValidationSchema,
})

const getSchema = (product: Product | null): yup.ObjectSchema<any, yup.AnyObject, any, ''> => {
  return product == Product.LiabilityInsurance ? schemaLiability : schema
}

const defaultValues: InsuranceParamsPropertyDto = {
  nextPage: 'next',
  generalParams: InitialGeneralParamsPropertyValues,
  policyHolder: InitialPolicyHolderValues,
  property: InitialPropertyValues('OkGroup'),
}

function isPlainObject(value: any): value is object {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  )
}

function customMerge<T extends object>(objA: T, objB: T): T {
  const result = {} as any
  // reset formulare z react-hook-form (v7.50.1. udajne tento problem vznikl ve verzi 7.22.*) dela nejaky interni merge objektu a undefined nebo null hodnoty ignoruje a nechava ve formulari puvodni.
  // Proto zde projdu vsechny hodnoty formulare a pokud je nemam v default values, tak je nastavim na vychozi hodnotu ('' nebo false)

  for (const key in objA) {
    const currentKey = key as keyof T
    if (isPlainObject(objA[currentKey])) {
      result[currentKey] = customMerge(
        objA[currentKey] as object,
        (objB[currentKey] as object) || {},
      )
    } else if (typeof objA[currentKey] === 'boolean') {
      result[currentKey] = currentKey in objB ? objB[currentKey] : false
    } else {
      result[currentKey] = currentKey in objB ? objB[currentKey] : ''
    }
  }

  return result
}

export const Step1Form = ({
  onSubmit,
  data = defaultValues,
}: Step1FormProps) => {
  const { product } = useApp()

  const defVals = useMemo(() => {
    return {
      ...defaultValues,
      property: product == Product.LiabilityInsurance ? InitialLiabilityValues('OkGroup') : InitialPropertyValues('OkGroup'),
    } as InsuranceParamsPropertyDto

  }, [product])

  const [isSubmittingCurrent, setIsSubmittingCurrent] = useState(false)
  const [isSubmittingNext, setIsSubmittingNext] = useState(false)
  const { triggerNewInsurance, clearTriggerNewInsurance, insuranceParamsId } =
    useInsuranceParamsContext()

  const { data: allInsurers } = useInsurers(false, null)

  const isContracted = !!data.generalParams.contractNumber

  const [validationSchema, setValidationSchema] = useState(getSchema(product!))

  const methods = useForm<InsuranceParamsPropertyDto>({
    defaultValues: data,
    resolver: yupResolver(validationSchema) as any,
  })

  const [isSubmitError, setIsSubmitError] = useState(false)

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    control,
    watch,
    setValue,
    getValues,
    setError,
    reset,
  } = methods

  const selectedType = watch('property.type')

  const resetDefaultValues = (defaultValues: InsuranceParamsPropertyDto) => {
    const defaultVals = customMerge(getValues(), defaultValues)
    defaultVals.property.insurers = allInsurers?.map((x) => x.id) ?? [] // // v default values nejsou vsechny pojistovny

    reset(defaultVals)
  }

  // pro pripad kdyz se z K1 klidne na 'Nové pojištění', tak se musi formular resetnout. Jinak nedochazelo k rerenderu
  useEffect(() => {
    if (triggerNewInsurance)
      resetDefaultValues(defVals)

    clearTriggerNewInsurance()
  }, [triggerNewInsurance, reset])

  const [isMounted, setIsMounted] = useState(false)
  useLayoutEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (!isMounted) return

    setValidationSchema(getSchema(product))

    if (product == Product.PropertyInsurance) {
      defVals.property.type = selectedType
    }

    // pokud by to delalo problemy, tak zvazit, zda neresetovat vzdy a ne jen pokud se jedna o novou nabidku
    if (!data.generalParams.paramsId)
      resetDefaultValues(defVals)
  }, [product])

  const submitForm = handleSubmit((data) => {
    const values = _.cloneDeep(data)
    // Datum poslu jen jako date bez casu. To by chtelo vyresit asi nejak obecne. Kdyz se to posle i s casem, tak dochazi k casovemu posunu, coz znamena na serveru predchozi den
    values.generalParams.insuranceStartDate = formatDate(
      values.generalParams.insuranceStartDate,
    )
    values.policyHolder.person.birthday = formatDate(
      values.policyHolder.person.birthday,
    )
    if (values.policyHolder.insuredPerson)
      values.policyHolder.insuredPerson.birthday = formatDate(
        values.policyHolder.insuredPerson.birthday,
      )
    if (values.property.data.concurrentInsuranceWithOtherInsurer) {
      values.property.data.concurrentHouseholdInsuranceValidTo = formatDate(values.property.data.concurrentHouseholdInsuranceValidTo)
      values.property.data.concurrentRealtyInsuranceValidTo = formatDate(values.property.data.concurrentRealtyInsuranceValidTo)
    }

    return onSubmit(values)
      .then(() => {
        setIsSubmitError(false)
        reset(values)

        if (values.nextPage == 'current') savedFormAlertOnOpen()
      })
      .catch((err) => {
        setIsSubmitError(true)

        if (
          !(err.response && err.response.status === 400 && err.response.data)
        ) {
          savedFormAlertOnOpen()
          return
        }

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })
      })
  })

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()

  useEffect(() => {
    if (!isSubmitting) {
      setIsSubmittingCurrent(false)
      setIsSubmittingNext(false)
    }
  }, [isSubmitting])

  const { isOpen: isOpenRealty } = useDisclosure({
    isOpen: !!selectedType && selectedType != RiskVariant.Household,
  })
  const { isOpen: isOpenHousehold } = useDisclosure({
    isOpen: !!selectedType && selectedType != RiskVariant.Realty,
  })

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <MainContent>
          <BaseParametersForm />

          {product == Product.PropertyInsurance &&
            <>
              <LocationForm />

              <CustomCollapse in={isOpenRealty}>
                <RealtyForm />
              </CustomCollapse>

              <CustomCollapse in={isOpenHousehold}>
                <HouseholdForm />
              </CustomCollapse>
            </>
          }

          <PolicyHolderForm />

          <ErrorSummary errors={errors} />

          {!isContracted && (
            <FormButtons
              rightButtons={
                <>
                  <Button
                    variant="outline"
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    isLoading={isSubmittingCurrent}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'current')
                      setIsSubmittingCurrent(true)
                      submitForm()
                    }}
                  >
                    Uložit
                  </Button>

                  <Button
                    colorScheme="primary"
                    isLoading={isSubmittingNext}
                    isDisabled={isSubmitting || isSubmittingNext || isSubmittingCurrent}
                    loadingText="Ukládám..."
                    type="button"
                    onClick={() => {
                      setValue('nextPage', 'next')
                      setIsSubmittingNext(true)
                      submitForm()
                    }}
                  >
                    <Check />
                    Uložit a pokračovat
                  </Button>

                  <SubmitDialog
                    isOpen={isSavedFormAlertOpen}
                    onClose={savedFormAlertOnClose}
                    colorScheme={!isSubmitError ? 'primary' : 'red'}
                    header={!isSubmitError ? 'Uloženo' : 'Chyba'}
                    body={
                      !isSubmitError
                        ? !insuranceParamsId
                          ? 'Nabídka byla uložena do rozpracovaných kalkulací'
                          : 'Nabídka byla úspěšně aktualizována'
                        : 'Při ukládání došlo k chybě'
                    }
                  />
                </>
              }
            />
          )}
        </MainContent>
      </form>
    </FormProvider>
  )
}

export const KNButton = () => (
  <Button
    variant="outline"
    as={Link}
    to="https://nahlizenidokn.cuzk.cz/"
    target="_blank"
  >
    <Search color="secondary.500" /> Náhled do katastru nemovitostí
  </Button>
)
